/**
 * CE QUE L'HISTORIQUE AFFICHE, calculé hors du rendu et testé.
 *
 * Même partage que `acgSummary.js` : un écran qui récapitule des séances
 * animées est lu comme un relevé, et un chiffre faux y est indétectable. Les
 * mises en forme délicates sont donc ici, pas enfouies dans un `map`.
 *
 * Les lignes viennent de `GET /my_sessions` déjà projetées côté serveur
 * (api/session_history.py) : ce module ne recalcule rien, il MET EN FORME.
 */

export const STATUS_LABELS = {
  terminee: 'Terminée',
  en_cours: 'En cours',
  abandonnee: 'Abandonnée',
};

// L'ordre des onglets d'état. `''` = pas de filtre, et il vient en premier :
// l'historique s'ouvre sur tout ce qui a été animé.
export const STATUS_TABS = [
  { value: '', label: 'Toutes' },
  { value: 'terminee', label: 'Terminées' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'abandonnee', label: 'Abandonnées' },
];

/**
 * LE RÉSULTAT D'UNE SÉANCE — registre fermé, comme `customPages.js`.
 *
 * `/my_sessions` rend TOUTES les valeurs de scores de la partie, sans leur
 * metadata : la base n'a pas à savoir lequel fait résultat. Mais un score n'est
 * pas lisible sans son intitulé ni son unité, et ceux-là ne sont pas dans la
 * ligne — d'où cette table. Ajouter un atelier dont le résultat se lit ailleurs
 * coûte une entrée ici, et rien d'autre.
 *
 * Le premier score présent gagne : un atelier n'a qu'un résultat de séance.
 */
export const RESULT_SCORES = [
  {
    id: 'water_projection',
    label: "Économies d'eau",
    // Le score que `game_summary.py` traite déjà comme le résultat d'une
    // séance Compte-Gouttes.
    format: (value) => `${Math.round(value)} pts`,
  },
  { id: 'cbs', label: 'CBS', format: (value) => value.toFixed(2) },
  {
    id: 'sum_actor',
    label: 'Satisfaction des acteurs',
    format: (value) => String(Math.round(value)),
  },
];

export function resultOf(session) {
  const scores = session?.scores || {};
  for (const entry of RESULT_SCORES) {
    const value = scores[entry.id];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return { label: entry.label, value: entry.format(value) };
    }
  }
  return null;
}

/**
 * Le titre d'une ligne. `name` est saisi au lancement et vide par défaut : on
 * retombe sur le nom de l'atelier plutôt que d'afficher une ligne anonyme, et
 * en dernier recours sur son identifiant — jamais rien.
 */
export function sessionTitle(session) {
  return (session?.name || '').trim()
    || (session?.config_name || '').trim()
    || session?.config_id
    || 'Séance sans nom';
}

function asDate(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(iso) {
  const date = asDate(iso);
  if (!date) return 'Date inconnue';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatDateTime(iso) {
  const date = asDate(iso);
  if (!date) return 'Date inconnue';
  return `${formatDate(iso)} à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * « il y a 3 jours ». Utilisé pour la DERNIÈRE ACTIVITÉ d'une séance non
 * terminée : c'est elle qui explique l'étiquette « abandonnée », et une date
 * absolue obligerait l'animateur à faire le calcul de tête.
 */
export function formatRelative(iso, now = new Date()) {
  const date = asDate(iso);
  if (!date) return '';
  const days = Math.floor((now - date) / 86400000);
  if (days < 0) return "à l'instant";
  if (days === 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 30) return `il y a ${days} jours`;
  const months = Math.floor(days / 30);
  if (months < 12) return `il y a ${months} mois`;
  const years = Math.floor(days / 365);
  return years === 1 ? 'il y a un an' : `il y a ${years} ans`;
}

export function formatMonth(month) {
  const [year, index] = String(month || '').split('-');
  if (!year || !index) return String(month || '');
  // Construit depuis les composants et non depuis la chaîne : `new Date('2026-09')`
  // est interprété en UTC et peut reculer d'un mois selon le fuseau.
  return new Date(Number(year), Number(index) - 1, 1)
    .toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
}

/**
 * LA SÉRIE MENSUELLE, TROUS COMPRIS.
 *
 * `by_month` ne contient que les mois où quelque chose a été animé. Dessiner
 * ces barres les unes à côté des autres ferait lire une activité continue là
 * où il y a eu six mois de silence — l'inverse de ce qu'un historique doit
 * montrer. On rend donc une suite CONTIGUË qui finit au mois courant, quitte
 * à ce que les dernières barres soient à zéro : un creux récent est une
 * information.
 */
export function monthSeries(byMonth, months = 12, now = new Date()) {
  const counts = new Map((byMonth || []).map((row) => [row.month, row]));
  const series = [];
  for (let offset = months - 1; offset >= 0; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const row = counts.get(key);
    series.push({
      month: key,
      label: formatMonth(key),
      count: row?.count || 0,
      nb_participants: row?.nb_participants || 0,
    });
  }
  return series;
}

/** La progression d'une séance en pourcentage entier, ou null. */
export function progressPercent(session) {
  const progress = session?.progress;
  if (typeof progress !== 'number' || !Number.isFinite(progress)) return null;
  return Math.round(100 * Math.min(1, Math.max(0, progress)));
}

/**
 * La query string d'un listing. Les paramètres vides sont OMIS et non envoyés
 * à blanc : `status=` serait rejeté côté serveur comme un état inconnu, et
 * rendrait toutes les séances au lieu d'aucune — un filtre qui ne filtre pas
 * est plus difficile à repérer qu'un filtre qui échoue.
 */
export function sessionsQuery({ status, configId, owner, includeTests, limit, skip } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (configId) params.set('config_id', configId);
  if (owner) params.set('owner_id', owner);
  if (includeTests) params.set('include_tests', 'true');
  if (limit) params.set('limit', String(limit));
  if (skip) params.set('skip', String(skip));
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Ce que la fiche envoie : les champs modifiés, dans la forme attendue. */
export function metaPayload(draft) {
  return {
    name: draft.name,
    is_test: Boolean(draft.is_test),
    context: {
      nb_participants: draft.nb_participants === '' ? null : draft.nb_participants,
      client: draft.client,
      location: draft.location,
      notes: draft.notes,
    },
  };
}

/** Le brouillon de fiche d'une séance, prêt pour des champs contrôlés. */
export function metaDraft(session) {
  const context = session?.context || {};
  return {
    name: session?.name || '',
    is_test: Boolean(session?.is_test),
    nb_participants: context.nb_participants == null ? '' : String(context.nb_participants),
    client: context.client || '',
    location: context.location || '',
    notes: context.notes || '',
  };
}
